import React, { useState, useEffect, useCallback, useRef } from 'react';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import ModalBusca from '../components/financeiro/ModalBuscaContaPagar';
import ModalBaixa from '../components/financeiro/ModalBaixaContaPagar';
import ModalNovaContaPagar from '../components/financeiro/ModalNovaContaPagar';
import api from '../services/api';
import { hojeBR } from '../utils/data';
import s from './PedidosOrcamentos.module.css';

const fmtMoeda = v => Number(v||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});
const fmtData  = str => { if (!str) return '—'; return new Date(String(str).slice(0,10)+'T12:00:00').toLocaleDateString('pt-BR'); };
const hoje     = () => hojeBR();

/* ── Autocomplete genérico ── */
function Autocomplete({ lista, value, onChange, placeholder, getLabelFn, getSearchFn, style, inputStyle }) {
  const [texto, setTexto]     = React.useState('');
  const [aberto, setAberto]   = React.useState(false);
  const [filtrado, setFiltrado] = React.useState([]);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    if (!value) { setTexto(''); return; }
    const item = lista.find(i => String(i.id) === String(value));
    if (item) setTexto(getLabelFn(item));
  }, [value, lista]); // eslint-disable-line

  React.useEffect(() => {
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const handleInput = (v) => {
    setTexto(v);
    if (!v) { onChange(''); setFiltrado([]); setAberto(false); return; }
    const q = v.toLowerCase();
    const r = lista.filter(i => getSearchFn(i).toLowerCase().includes(q)).slice(0, 20);
    setFiltrado(r);
    setAberto(true);
  };

  const handleSelect = (item) => {
    setTexto(getLabelFn(item));
    onChange(String(item.id));
    setAberto(false);
  };

  const handleClear = () => { setTexto(''); onChange(''); setAberto(false); };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setAberto(false); return; }
    if (e.key === 'Enter' && aberto) {
      e.preventDefault();
      e.stopPropagation();
      setAberto(false);
    }
    // Enter com dropdown fechado: deixa borbulhar para o navEnter do pai
  };

  const base = { width:'100%', padding:'4px 24px 4px 8px', border:'1px solid #d1d5db',
    borderRadius:4, fontSize:13, height:26, outline:'none' };

  return (
    <div ref={wrapRef} style={{position:'relative', ...style}}>
      <div style={{display:'flex',alignItems:'center',position:'relative'}}>
        <input
          value={texto}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => texto && filtrado.length && setAberto(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{...base, ...inputStyle}}
        />
        {texto && (
          <button onClick={handleClear}
            style={{position:'absolute',right:4,background:'none',border:'none',
              cursor:'pointer',color:'#9ca3af',fontSize:14,lineHeight:1,padding:0}}>×</button>
        )}
      </div>
      {aberto && filtrado.length > 0 && (
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:200,
          background:'#fff',border:'1px solid #d1d5db',borderRadius:4,
          boxShadow:'0 4px 12px rgba(0,0,0,0.15)',maxHeight:220,overflowY:'auto'}}>
          {filtrado.map(item => (
            <div key={item.id} onMouseDown={() => handleSelect(item)}
              style={{padding:'5px 10px',cursor:'pointer',fontSize:12,borderBottom:'1px solid #f3f4f6'}}
              onMouseEnter={e=>e.currentTarget.style.background='#EBF5FF'}
              onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
              {getLabelFn(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL = {
  aberto:    { txt: 'Aberto',    cls: s.badgeAberto    },
  vencido:   { txt: 'Vencido',   cls: s.badgeCancelado },
  pago:      { txt: 'Pago',      cls: s.badgeAprovado  },
  cancelado: { txt: 'Cancelado', cls: s.badgeCancelado },
};

const statusEfetivo = (cp) => {
  if (cp.status === 'pago' || cp.status === 'cancelado') return cp.status;
  if (cp.data_vencimento && cp.data_vencimento < hoje()) return 'vencido';
  return 'aberto';
};

const COR_STATUS = {
  aberto:    '#1565c0',
  vencido:   '#b71c1c',
  pago:      '#2e7d32',
  cancelado: '#888',
};

const FORM_VAZIO = {
  id: null, descricao: '', valor: '', numero_documento: '',
  parcela: '', total_parcelas: '',
  data_emissao: hoje(), data_vencimento: '', data_pagamento: '',
  fornecedor_id: '', fornecedor_nome: '',
  conta_id: '', plano_conta_id: '', observacoes: '', status: 'aberto',
};

/* ════════════════════════════════════════
   Tela principal — Contas a Pagar
════════════════════════════════════════ */
export default function ContasPagar() {
  const [lista, setLista]           = useState([]);
  const [form, setForm]             = useState(FORM_VAZIO);
  const [modo, setModo]             = useState('nav');   // nav | novo | editar
  const [posicao, setPosicao]       = useState(0);
  const [total, setTotal]           = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [erroMsg, setErroMsg]       = useState('');
  const [resumo, setResumo]         = useState({});

  // Filtros da lista
  const [filtros, setFiltros]       = useState({ status:'aberto', busca:'', venc_de:'2000-01-01', venc_ate:'2099-12-31', fornecedor_id:'' });
  // Campo de data com texto que não é data de verdade (ex.: 31/09) — o input
  // devolve value='' nesse caso, o que viraria "sem limite" no filtro.
  const [datasInvalidas, setDatasInvalidas] = useState({ de:false, ate:false });
  const [chaveDatas, setChaveDatas] = useState(0);
  const [selId, setSelId]           = useState(null);

  // Fornecedores, contas e plano de contas
  const [fornecedores, setFornecedores] = useState([]);
  const [contasFinanc, setContasFinanc] = useState([]);
  const [planoContas,  setPlanoContas]  = useState([]);

  // Modais
  const [modalBusca, setModalBusca]   = useState(false);
  const [modalBaixa, setModalBaixa]   = useState(false);
  const [modalNovo,  setModalNovo]    = useState(false);

  const editando = modo !== 'nav';

  /* ── Refs e navegação por Enter ── */
  const formRef     = useRef(null);
  const primeiroRef = useRef(null);
  const salvarRef   = useRef(null);

  useEffect(() => { salvarRef.current = handleSalvar; });

  useEffect(() => {
    if (modo === 'editar') {
      setTimeout(() => primeiroRef.current?.focus(), 80);
    }
  }, [modo]);

  const navEnter = useCallback((e) => {
    if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.altKey) return;
    const campos = Array.from(
      formRef.current?.querySelectorAll('input:not([readonly]):not([disabled]), select:not([disabled])') || []
    );
    const idx = campos.indexOf(e.target);
    if (idx >= 0 && idx < campos.length - 1) {
      e.preventDefault();
      campos[idx + 1].focus();
    } else if (idx === campos.length - 1) {
      e.preventDefault();
      salvarRef.current?.();
    }
  }, []);

  /* ── Carregar dados auxiliares ── */
  useEffect(() => {
    api.get('/fornecedores').then(r=>setFornecedores(r.data||[])).catch(()=>{});
    api.get('/financeiro/contas').then(r=>setContasFinanc(r.data||[])).catch(()=>{});
    api.get('/financeiro/plano-contas/analiticas', { params: { tipo: 'despesa,custo' } })
       .then(r=>setPlanoContas(r.data||[])).catch(()=>{});
    carregarResumo();
  }, []);

  const carregarResumo = async () => {
    try { const {data}=await api.get('/financeiro/contas-pagar/resumo'); setResumo(data||{}); } catch {}
  };

  /* ── Carregar lista ── */
  const carregarLista = useCallback(async (f=filtros) => {
    setCarregando(true);
    try {
      const { data } = await api.get('/financeiro/contas-pagar', { params: f });
      setLista(data);
      setTotal(data.length);
      return data;
    } catch { return []; }
    finally { setCarregando(false); }
  }, [filtros]);

  useEffect(() => { carregarLista(); }, []); // eslint-disable-line

  /* ── Abrir registro ── */
  const abrirConta = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/financeiro/contas-pagar/${id}`);
      setForm({
        ...FORM_VAZIO, ...data,
        valor:           data.valor || '',
        parcela:         data.parcela || '',
        total_parcelas:  data.total_parcelas || '',
        data_emissao:    data.data_emissao?.slice(0,10) || hoje(),
        data_vencimento: data.data_vencimento?.slice(0,10) || '',
        data_pagamento:  data.data_pagamento?.slice(0,10) || '',
        fornecedor_id:   data.fornecedor_id || '',
        conta_id:        data.conta_id || '',
        plano_conta_id:  data.plano_conta_id || '',
      });
      setModo('nav');
      setErroMsg('');
    } catch { setErroMsg('Erro ao carregar conta.'); }
  }, []);

  /* ── Navegação ── */
  const navPara = useCallback(async (idx, arr=lista) => {
    if (!arr.length) return;
    const i = Math.max(0, Math.min(idx, arr.length-1));
    setPosicao(i);
    await abrirConta(arr[i].id);
  }, [lista, abrirConta]);

  /* ── Novo ── */
  /* ── Novo: lançamento manual em modal (F4) — título avulso, sem vínculo
     com entrada de NF (que gera duplicatas na confirmação) ── */
  const handleNovo = () => { if (!editando) setModalNovo(true); };

  const handleNovoSalvo = async (novoId) => {
    setModalNovo(false);
    const arr = await carregarLista();
    const idx = arr.findIndex(x => x.id === novoId);
    if (idx >= 0) { setPosicao(idx); await abrirConta(novoId); }
    carregarResumo();
  };

  /* ── Alterar ── */
  const handleAlterar = () => { setModo('editar'); setErroMsg(''); };

  /* ── Cancelar ── */
  const handleCancelar = () => {
    setErroMsg('');
    if (form.id) { setModo('nav'); abrirConta(form.id); }
    else { setForm(FORM_VAZIO); setModo('nav'); }
  };

  /* ── Salvar ── */
  const handleSalvar = async () => {
    if (!form.descricao.trim()) return setErroMsg('Descrição é obrigatória.');
    if (!form.valor || parseFloat(form.valor)<=0) return setErroMsg('Informe um valor válido.');
    if (!form.data_vencimento) return setErroMsg('Data de vencimento é obrigatória.');
    setSalvando(true); setErroMsg('');
    try {
      const payload = {
        descricao:        form.descricao.trim(),
        valor:            parseFloat(form.valor),
        numero_documento: form.numero_documento || null,
        parcela:          form.parcela ? parseInt(form.parcela) : null,
        total_parcelas:   form.total_parcelas ? parseInt(form.total_parcelas) : null,
        data_emissao:     form.data_emissao || hoje(),
        data_vencimento:  form.data_vencimento,
        data_pagamento:   form.data_pagamento || null,
        fornecedor_id:    form.fornecedor_id || null,
        conta_id:         form.conta_id || null,
        plano_conta_id:   form.plano_conta_id || null,
        observacoes:      form.observacoes || null,
        status:           form.status || 'aberto',
      };
      if (form.id) {
        await api.put(`/financeiro/contas-pagar/${form.id}`, payload);
      } else {
        const { data } = await api.post('/financeiro/contas-pagar', payload);
        payload.id = data.id;
        setForm(f=>({...f, id: data.id}));
      }
      const arr = await carregarLista();
      const idx = arr.findIndex(x => x.id === (form.id || payload.id));
      if (idx>=0) { setPosicao(idx); await abrirConta(arr[idx].id); }
      setModo('nav');
      carregarResumo();
    } catch (err) {
      setErroMsg(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  /* ── Baixar ── */
  const handleBaixar = async (dados) => {
    try {
      const { data } = await api.post(`/financeiro/contas-pagar/${form.id}/baixar`, dados);
      setModalBaixa(false);
      const arr = await carregarLista();
      carregarResumo();
      if (data.parcial && data.restante) {
        // Navega para a conta residual gerada
        const idx = arr.findIndex(x => x.id === data.restante.id);
        if (idx >= 0) { setPosicao(idx); await abrirConta(data.restante.id); }
      } else {
        await abrirConta(form.id);
      }
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao baixar conta.');
    }
  };

  /* ── Excluir ── */
  const handleExcluir = async () => {
    if (!form.id) return;
    if (!window.confirm(`Excluir "${form.descricao}"?`)) return;
    try {
      await api.delete(`/financeiro/contas-pagar/${form.id}`);
      const arr = await carregarLista();
      if (arr.length) { setPosicao(0); await abrirConta(arr[0].id); }
      else { setForm(FORM_VAZIO); setModo('nav'); }
      carregarResumo();
    } catch (err) {
      setErroMsg(err.response?.data?.erro || 'Erro ao excluir.');
    }
  };

  /* ── Filtro ── */
  const handleFiltro = async (patch, invalidas = datasInvalidas) => {
    const nf = {...filtros,...patch};
    setFiltros(nf);
    if (invalidas.de || invalidas.ate) { setLista([]); setTotal(0); setForm(FORM_VAZIO); return; }
    const arr = await carregarLista(nf);
    if (arr.length) { setPosicao(0); await abrirConta(arr[0].id); }
    else { setForm(FORM_VAZIO); }
  };
  const mudarData = (campo, e) => {
    const inv = { ...datasInvalidas, [campo === 'venc_de' ? 'de' : 'ate']: e.target.value === '' && e.target.validity?.badInput };
    setDatasInvalidas(inv);
    handleFiltro({ [campo]: e.target.value }, inv);
  };

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const stEf = form.id ? statusEfetivo(form) : 'aberto';
  const stLabel = STATUS_LABEL[stEf] || STATUS_LABEL.aberto;

  const podeBaixar = form.id && stEf !== 'pago' && stEf !== 'cancelado' && !editando;

  /* ── Atalhos de teclado: F4 novo, Ctrl+G foca a grade, ↑/↓ navegam, Ctrl+R baixa ── */
  const gradeRef = useRef(null);
  useEffect(() => {
    const h = e => {
      if (editando || modalBaixa || modalNovo) return;
      if (e.key === 'F4') {
        e.preventDefault();
        handleNovo();
        return;
      }
      if (e.ctrlKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        gradeRef.current?.focus();
        if (lista.length && posicao < 0) navPara(0);
        return;
      }
      if (e.ctrlKey && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        if (podeBaixar) setModalBaixa(true);
        return;
      }
      const tag = document.activeElement?.tagName;
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
          !['INPUT', 'SELECT', 'TEXTAREA'].includes(tag)) {
        if (!lista.length) return;
        e.preventDefault();
        const n = e.key === 'ArrowDown'
          ? Math.min(posicao + 1, lista.length - 1)
          : Math.max(posicao - 1, 0);
        if (n !== posicao) navPara(n);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });
  const podeCancelarStatus = form.id && stEf !== 'cancelado' && stEf !== 'pago' && !editando;

  /* ── Estilos de edição destacada ── */
  const iSt = editando
    ? { fontSize: 14, height: 30, padding: '4px 8px', borderColor: '#1976d2', background: '#fff', fontWeight: 500 }
    : {};
  const lSt = editando ? { fontSize: 12, fontWeight: 700, color: '#1565c0' } : {};

  /* ── Campo auxiliar ── */
  const campo = (label, k, w='100%', tipo='text', extra={}) => {
    const { style: extraStyle, ...restExtra } = extra;
    return (
      <div style={{display:'flex',flexDirection:'column',gap:3,width:w}}>
        <label className={s.labelErp} style={lSt}>{label}</label>
        <input className={s.inputErp} type={tipo} value={form[k]||''}
          onChange={e=>set(k,e.target.value)} readOnly={!editando}
          style={{...iSt,...(extraStyle||{})}} onKeyDown={navEnter}
          {...restExtra} />
      </div>
    );
  };

  return (
    <div className={s.erpWrap}>
      {/* ── Barra de título ── */}
      <div className={s.titleBar}>
        <span>💳</span> Contas a Pagar
      </div>

      {/* ── Painel de resumo (cards) ── */}
      <div style={{display:'flex',gap:12,padding:'10px 12px',background:'#e8eaf0',borderBottom:'1px solid #c0c8d8',flexWrap:'wrap'}}>
        {[
          { rot: 'Em aberto',   val: resumo.total_aberto,   extra: `${resumo.qtd_aberto||0} conta(s)`,  cor: '#1565c0', bg: '#eff6ff', bd: '#93c5fd' },
          { rot: 'Vencido',     val: resumo.total_vencido,  extra: `${resumo.qtd_vencido||0} conta(s)`, cor: '#b71c1c', bg: '#fef2f2', bd: '#fca5a5' },
          { rot: 'Pago no mês', val: resumo.total_pago_mes, extra: null, cor: '#2e7d32', bg: '#f0fdf4', bd: '#86efac' },
        ].map(c => (
          <div key={c.rot} style={{background:c.bg,border:`1px solid ${c.bd}`,borderRadius:6,
            padding:'8px 16px',minWidth:170}}>
            <div style={{fontSize:12,fontWeight:700,color:c.cor,textTransform:'uppercase'}}>{c.rot}</div>
            <div style={{fontSize:18,fontWeight:700,color:c.cor}}>{fmtMoeda(c.val)}</div>
            {c.extra && <div style={{fontSize:12,color:'#666'}}>{c.extra}</div>}
          </div>
        ))}
      </div>

      {/* ── Filtros rápidos ── */}
      <div style={{display:'flex',gap:8,padding:'7px 12px',background:'#f5f5f5',borderBottom:'1px solid #ddd',flexWrap:'wrap',alignItems:'center'}}>
        <label style={{fontSize:13,fontWeight:600}}>Status:</label>
        {['aberto','vencido','pago','todos'].map(st=>(
          <button key={st}
            className={`${s.btnErp} ${filtros.status===st?s.btnErpPrimario:''}`}
            style={{fontSize:13,padding:'4px 12px'}}
            onClick={()=>handleFiltro({status:st})}>
            {st.charAt(0).toUpperCase()+st.slice(1)}
          </button>
        ))}
        <div style={{marginLeft:8,display:'flex',gap:4,alignItems:'center'}}>
          <label style={{fontSize:13}}>Venc. de</label>
          <input key={`de${chaveDatas}`} type="date" className={s.inputErp}
            style={{width:130,height:26,fontSize:13,...(datasInvalidas.de?{borderColor:'#c62828',background:'#ffebee'}:{})}}
            value={filtros.venc_de} onChange={e=>mudarData('venc_de', e)} />
          <label style={{fontSize:13}}>até</label>
          <input key={`ate${chaveDatas}`} type="date" className={s.inputErp}
            style={{width:130,height:26,fontSize:13,...(datasInvalidas.ate?{borderColor:'#c62828',background:'#ffebee'}:{})}}
            value={filtros.venc_ate} onChange={e=>mudarData('venc_ate', e)} />
          {(datasInvalidas.de || datasInvalidas.ate) && (
            <span style={{fontSize:12,color:'#c62828',fontWeight:600}}
              title="Ex.: setembro, abril, junho e novembro têm 30 dias; fevereiro, 28 ou 29">
              ⚠ Data inválida no período
            </span>
          )}
          <button className={s.btnErp} style={{fontSize:13,padding:'4px 12px'}}
            onClick={()=>{ const ok={de:false,ate:false}; setDatasInvalidas(ok); setChaveDatas(k=>k+1);
              handleFiltro({venc_de:'',venc_ate:'',fornecedor_id:''}, ok); }}>Limpar</button>
        </div>
        <div style={{display:'flex',gap:4,alignItems:'center'}}>
          <label style={{fontSize:13}}>Fornecedor</label>
          <Autocomplete
            lista={fornecedores}
            value={filtros.fornecedor_id}
            onChange={v => handleFiltro({fornecedor_id:v})}
            placeholder="Nome ou CNPJ/CPF..."
            getLabelFn={f => f.nome_fantasia || f.razao_social}
            getSearchFn={f => `${f.nome_fantasia||''} ${f.razao_social||''} ${f.cpf_cnpj||''}`}
            style={{minWidth:300}}
          />
        </div>
      </div>

      {/* ── Formulário ── */}
      <div ref={formRef} onKeyDown={navEnter}
        className={s.headerRow}
        style={{gap:8, padding:'8px 12px', flexWrap:'wrap',
          ...(editando ? {background:'#f0f7ff', borderBottom:'2px solid #1976d2', boxShadow:'inset 0 -2px 0 #1565c0'} : {})}}>

        {/* Linha 1 */}
        <div style={{display:'flex',gap:8,width:'100%',alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:60}}>
            <label className={s.labelErp} style={lSt}>Código</label>
            <input className={s.inputErp} readOnly value={form.id||''} style={{...iSt, background:'#f0f0f0'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:1,minWidth:260}}>
            <label className={s.labelErp} style={lSt}>Descrição *</label>
            <input ref={primeiroRef} className={s.inputErp} value={form.descricao||''}
              onChange={e=>set('descricao',e.target.value)} readOnly={!editando}
              style={{...iSt, ...(editando ? {borderColor:'#0d47a1', borderWidth:2} : {})}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:130}}>
            <label className={s.labelErp} style={lSt}>Nº Documento</label>
            <input className={s.inputErp} value={form.numero_documento||''}
              onChange={e=>set('numero_documento',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:60}}>
            <label className={s.labelErp} style={lSt}>Parcela</label>
            <input className={s.inputErp} type="number" min="1" value={form.parcela||''}
              onChange={e=>set('parcela',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:60}}>
            <label className={s.labelErp} style={lSt}>de</label>
            <input className={s.inputErp} type="number" min="1" value={form.total_parcelas||''}
              onChange={e=>set('total_parcelas',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:110}}>
            <label className={s.labelErp} style={lSt}>Valor R$ *</label>
            <input className={s.inputErp} type="number" step="0.01" value={form.valor||''}
              onChange={e=>set('valor',e.target.value)} readOnly={!editando}
              style={{...iSt, textAlign:'right', fontWeight:700,
                ...(editando ? {fontSize:15, borderColor:'#0d47a1', borderWidth:2} : {})}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            <label className={s.labelErp} style={lSt}>Status</label>
            <span className={`${s.badge} ${stLabel.cls}`} style={{alignSelf:'flex-start',marginTop:4}}>{stLabel.txt}</span>
          </div>
        </div>

        {/* Linha 2 */}
        <div style={{display:'flex',gap:8,width:'100%',alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:1,minWidth:220}}>
            <label className={s.labelErp} style={lSt}>Fornecedor</label>
            {editando ? (
              <Autocomplete
                lista={fornecedores}
                value={form.fornecedor_id||''}
                onChange={v => set('fornecedor_id', v)}
                placeholder="Nome ou CNPJ/CPF..."
                getLabelFn={f => `${f.codigo ? f.codigo+' — ' : ''}${f.nome_fantasia||f.razao_social}`}
                getSearchFn={f => `${f.nome_fantasia||''} ${f.razao_social||''} ${f.cpf_cnpj||''}`}
                style={{width:'100%'}} inputStyle={iSt}
              />
            ) : (
              <input className={s.inputErp} readOnly style={iSt}
                value={form.fornecedor_id ? (fornecedores.find(f=>String(f.id)===String(form.fornecedor_id))?.nome_fantasia || '—') : '—'} />
            )}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:130}}>
            <label className={s.labelErp} style={lSt}>Emissão</label>
            <input className={s.inputErp} type="date" value={form.data_emissao||''}
              onChange={e=>set('data_emissao',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:130}}>
            <label className={s.labelErp} style={lSt}>Vencimento *</label>
            <input className={s.inputErp} type="date" value={form.data_vencimento||''}
              onChange={e=>set('data_vencimento',e.target.value)} readOnly={!editando}
              style={{...iSt, ...(stEf==='vencido' ? {borderColor:'#e53935',color:'#b71c1c'} : editando ? {borderColor:'#0d47a1',borderWidth:2} : {})}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,width:130}}>
            <label className={s.labelErp} style={lSt}>Pagamento</label>
            <input className={s.inputErp} type="date" value={form.data_pagamento||''}
              onChange={e=>set('data_pagamento',e.target.value)} readOnly={!editando}
              style={{...iSt, ...(form.data_pagamento ? {color:'#2e7d32'} : {})}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:1,minWidth:180}}>
            <label className={s.labelErp} style={lSt}>Conta Financeira</label>
            <select className={s.selectErp} value={form.conta_id||''} disabled={!editando}
              onChange={e=>set('conta_id',e.target.value)}
              style={editando ? {fontSize:14, height:30, borderColor:'#1976d2'} : {}}>
              <option value="">— Selecione —</option>
              {contasFinanc.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
        </div>

        {/* Linha 3 — Categoria + Observações */}
        <div style={{display:'flex',gap:8,width:'100%',alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:1,minWidth:280}}>
            <label className={s.labelErp} style={lSt}>Categoria (Plano de Contas)</label>
            {editando ? (
              <Autocomplete
                lista={planoContas}
                value={form.plano_conta_id||''}
                onChange={v => set('plano_conta_id', v)}
                placeholder="Buscar por código ou descrição..."
                getLabelFn={pc => `${pc.codigo} — ${pc.descricao}`}
                getSearchFn={pc => `${pc.codigo} ${pc.descricao}`}
                style={{width:'100%'}} inputStyle={iSt}
              />
            ) : (
              <input className={s.inputErp} readOnly
                value={form.plano_conta_id
                  ? (planoContas.find(pc=>String(pc.id)===String(form.plano_conta_id))
                      ? `${planoContas.find(pc=>String(pc.id)===String(form.plano_conta_id)).codigo} — ${planoContas.find(pc=>String(pc.id)===String(form.plano_conta_id)).descricao}`
                      : form.plano_codigo ? `${form.plano_codigo} — ${form.plano_descricao}` : '—')
                  : '—'}
                style={{color: form.plano_conta_id ? '#1565c0' : '#888'}}
              />
            )}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:2,minWidth:200}}>
            <label className={s.labelErp} style={lSt}>Observações</label>
            <input className={s.inputErp} value={form.observacoes||''}
              onChange={e=>set('observacoes',e.target.value)} readOnly={!editando} style={iSt} />
          </div>
        </div>
      </div>

      {/* ── Mini-lista (grid lateral) — Ctrl+G foca, ↑/↓ navegam ── */}
      <div ref={gradeRef} tabIndex={0}
        title="Ctrl+G foca a grade · ↑/↓ selecionam o documento · Ctrl+R abre o Baixar"
        style={{flex:1,overflowY:'auto',margin:'0 12px 4px',outline:'none'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead style={{position:'sticky',top:0,background:'#dde3ef',zIndex:1}}>
            <tr>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:50,textAlign:'left'}}>Cód.</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',textAlign:'left'}}>Descrição</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',textAlign:'left'}}>Fornecedor</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:90}}>Emissão</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:90}}>Vencimento</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:90}}>Pagamento</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:110}}>Forma Pagto</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:110,textAlign:'right'}}>Valor</th>
              <th style={{padding:'3px 6px',border:'1px solid #c0c8d8',width:80}}>Status</th>
            </tr>
          </thead>
          <tbody>
            {carregando&&<tr><td colSpan={9} style={{textAlign:'center',padding:10,color:'#888'}}>Carregando...</td></tr>}
            {!carregando&&lista.length===0&&<tr><td colSpan={9} style={{textAlign:'center',padding:10,color:'#888',fontStyle:'italic'}}>
              {(datasInvalidas.de || datasInvalidas.ate) ? 'Corrija a data do período para filtrar.' : 'Nenhuma conta encontrada.'}</td></tr>}
            {lista.map((c,i)=>{
              const stKey = statusEfetivo(c);
              const st = STATUS_LABEL[stKey] || STATUS_LABEL.aberto;
              const ativo = form.id===c.id;
              return (
                <tr key={c.id}
                  style={{cursor:'pointer', color: COR_STATUS[stKey]||'inherit', background: ativo?'#cce3ff': i%2===0?'#fff':'#f8f9fc'}}
                  onClick={async()=>{ setPosicao(i); await abrirConta(c.id); }}
                >
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec'}}>{c.id}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec'}}>{c.descricao}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec'}}>{c.fornecedor_nome||'—'}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center'}}>{fmtData(c.data_emissao)}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center'}}>{fmtData(c.data_vencimento)}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center',color:c.data_pagamento?'#2e7d32':'inherit'}}>{fmtData(c.data_pagamento)}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'center'}}>{c.forma_pagamento||'—'}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec',textAlign:'right',fontWeight:600}}>{fmtMoeda(c.valor)}</td>
                  <td style={{padding:'2px 6px',border:'1px solid #e0e4ec'}}>
                    <span className={`${s.badge} ${st.cls}`}>{st.txt}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {lista.length>0&&(
            <tfoot>
              <tr style={{background:'#eef2fa',fontWeight:600}}>
                <td colSpan={7} style={{padding:'3px 6px',border:'1px solid #c0c8d8',textAlign:'right',fontSize:12}}>Total filtrado:</td>
                <td style={{padding:'3px 6px',border:'1px solid #c0c8d8',textAlign:'right',fontSize:12}}>
                  {fmtMoeda(lista.reduce((s,c)=>s+parseFloat(c.valor||0),0))}
                </td>
                <td style={{border:'1px solid #c0c8d8'}}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── Status bar ── */}
      <div className={s.statusBar}>
        {carregando ? 'Carregando...' : `${total} registro(s)`}
        {erroMsg && <span className={s.statusErro} style={{marginLeft:12}}>{erroMsg}</span>}
      </div>

      {/* ── Footer ── */}
      <div className={s.footer}>
        <div className={s.footerLeft}>
          <button className={s.btnErpNav} disabled={editando||posicao===0} onClick={()=>navPara(0)}>|◄</button>
          <button className={s.btnErpNav} disabled={editando||posicao===0} onClick={()=>navPara(posicao-1)}>◄</button>
          <span style={{fontSize:12,margin:'0 4px'}}>{lista.length?posicao+1:0} / {total}</span>
          <button className={s.btnErpNav} disabled={editando||posicao>=total-1} onClick={()=>navPara(posicao+1)}>►</button>
          <button className={s.btnErpNav} disabled={editando||posicao>=total-1} onClick={()=>navPara(total-1)}>►|</button>
        </div>
        <div className={s.footerRight}>
          {podeBaixar && (
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={()=>setModalBaixa(true)}
              title="Baixar (pagar) o documento selecionado (Ctrl+R)">
              💰 Baixar <span style={{opacity:.75,fontWeight:400,fontSize:11}}>Ctrl+R</span>
            </button>
          )}
          {podeCancelarStatus && (
            <button className={`${s.btnErp} ${s.btnErpPerigo}`} onClick={async()=>{
              if (!window.confirm('Cancelar esta conta?')) return;
              const senha = await pedirSenhaAdmin();
              if (senha === null) return;
              try {
                await api.put(`/financeiro/contas-pagar/${form.id}`, { ...form, status:'cancelado', senha_admin:senha, valor:parseFloat(form.valor), fornecedor_id:form.fornecedor_id||null, conta_id:form.conta_id||null });
                await abrirConta(form.id); carregarLista(); carregarResumo();
              } catch(err){ setErroMsg(err.response?.data?.erro||'Erro.'); }
            }}>Cancelar Conta</button>
          )}
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={handleNovo} disabled={editando}
            title="Lançamento manual de conta a pagar (F4)">Novo <span style={{opacity:.75,fontWeight:400,fontSize:11}}>F4</span></button>
          <button className={s.btnErp} onClick={()=>setModalBusca(true)} disabled={editando}>Buscar</button>
          <button className={s.btnErp} onClick={handleAlterar} disabled={editando||!form.id||stEf==='pago'||stEf==='cancelado'}>Alterar</button>
          <button className={s.btnErp} onClick={handleCancelar} disabled={!editando}>Cancelar</button>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={handleSalvar} disabled={!editando||salvando}>
            {salvando?'Salvando...':'Salvar'}
          </button>
          <button className={`${s.btnErp} ${s.btnErpPerigo}`} onClick={handleExcluir}
            disabled={editando||!form.id||stEf==='pago'}>Excluir</button>
          <div className={s.footerSep}/>
          <button className={s.btnErp} onClick={()=>window.history.back()}>Sair</button>
        </div>
      </div>

      {/* ── Modais ── */}
      {modalBusca && (
        <ModalBusca
          onClose={()=>setModalBusca(false)}
          onSelect={async(c)=>{
            setModalBusca(false);
            const idx=lista.findIndex(x=>x.id===c.id);
            setPosicao(idx>=0?idx:0);
            await abrirConta(c.id);
            // Leva o fornecedor da busca para o filtro (grade só com ele)
            if (c.fornecedor_id) handleFiltro({ fornecedor_id: String(c.fornecedor_id) });
          }}
        />
      )}
      {modalBaixa && form.id && (
        <ModalBaixa
          conta={form}
          contas={contasFinanc}
          onBaixar={handleBaixar}
          onClose={()=>setModalBaixa(false)}
        />
      )}
      {modalNovo && (
        <ModalNovaContaPagar
          fornecedores={fornecedores}
          contasFinanc={contasFinanc}
          planoContas={planoContas}
          onSalvo={handleNovoSalvo}
          onClose={()=>setModalNovo(false)}
        />
      )}
    </div>
  );
}
